import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Activity, BodyShape, HeightBand, PainPoint, QuizAnswers, QuizResult } from '@/types/quiz'
import { scoreQuiz } from '@/lib/scoring'
import { SESSION_STORAGE_KEY } from '@/lib/config'

interface QuizState {
  activity: Activity | null
  painPoints: PainPoint[]
  bodyShape: BodyShape | null
  heightBand: HeightBand | null
}

interface QuizContextValue extends QuizState {
  setActivity: (activity: Activity) => void
  togglePainPoint: (pain: PainPoint) => void
  setBodyShape: (shape: BodyShape) => void
  setHeightBand: (band: HeightBand) => void
  reset: () => void
  /** True when all four answers are filled in - safe to score. */
  isComplete: boolean
  /** Compute the quiz result. Throws if isComplete === false. */
  getResult: () => QuizResult
  /** The filled-in answers, or null if incomplete. */
  asAnswers: () => QuizAnswers | null
}

const EMPTY: QuizState = { activity: null, painPoints: [], bodyShape: null, heightBand: null }

const QuizContext = createContext<QuizContextValue | null>(null)

export function QuizProvider({ children }: { children: ReactNode }) {
  // Always start fresh on page load. Previously we rehydrated from
  // sessionStorage so a mid-quiz refresh would pick up where the user left
  // off, but Maria prefers refresh = clean slate. The useState initialiser
  // runs exactly once per mount, which is where we also nuke any legacy
  // saved state so users who had the old behaviour don't get stuck with
  // ghost selections.
  const [state, setState] = useState<QuizState>(() => {
    if (typeof window !== 'undefined') {
      try { window.sessionStorage.removeItem(SESSION_STORAGE_KEY) } catch { /* noop */ }
    }
    return EMPTY
  })

  const setActivity = useCallback((activity: Activity) => {
    setState((prev) => {
      // Changing activity can shift the regular/short cm cutoff. If the
      // user already picked a height under the old activity, drop it so
      // they're forced to confirm under the new bands rather than carry
      // a now-mislabelled selection silently into the result.
      if (prev.activity && prev.activity !== activity) {
        return { ...prev, activity, heightBand: null }
      }
      return { ...prev, activity }
    })
  }, [])

  const togglePainPoint = useCallback((pain: PainPoint) => {
    setState((prev) => {
      const has = prev.painPoints.includes(pain)
      // "None of the above" is exclusive - toggling it clears other selections,
      // and toggling anything else clears "none".
      if (pain === 'none') {
        return { ...prev, painPoints: has ? [] : ['none'] }
      }
      const next = has
        ? prev.painPoints.filter((p) => p !== pain)
        : [...prev.painPoints.filter((p) => p !== 'none'), pain]
      return { ...prev, painPoints: next }
    })
  }, [])

  const setBodyShape = useCallback((shape: BodyShape) => {
    setState((prev) => ({ ...prev, bodyShape: shape }))
  }, [])

  const setHeightBand = useCallback((band: HeightBand) => {
    setState((prev) => ({ ...prev, heightBand: band }))
  }, [])

  const reset = useCallback(() => {
    setState(EMPTY)
    try {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY)
    } catch { /* noop */ }
  }, [])

  const isComplete = !!(state.activity && state.bodyShape && state.heightBand && state.painPoints.length > 0)

  const asAnswers = useCallback((): QuizAnswers | null => {
    if (!state.activity || !state.bodyShape || !state.heightBand || state.painPoints.length === 0) return null
    return {
      activity:   state.activity,
      painPoints: state.painPoints,
      bodyShape:  state.bodyShape,
      heightBand: state.heightBand,
    }
  }, [state])

  const getResult = useCallback((): QuizResult => {
    const a = asAnswers()
    if (!a) throw new Error('getResult() called before quiz was complete')
    return scoreQuiz(a)
  }, [asAnswers])

  const value = useMemo<QuizContextValue>(() => ({
    ...state,
    setActivity,
    togglePainPoint,
    setBodyShape,
    setHeightBand,
    reset,
    isComplete,
    getResult,
    asAnswers,
  }), [state, setActivity, togglePainPoint, setBodyShape, setHeightBand, reset, isComplete, getResult, asAnswers])

  return <QuizContext.Provider value={value}>{children}</QuizContext.Provider>
}

export function useQuiz(): QuizContextValue {
  const ctx = useContext(QuizContext)
  if (!ctx) throw new Error('useQuiz() must be used inside <QuizProvider>')
  return ctx
}
